import type { MouseEvent } from 'react';

import { ChevronDown } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';

import { Box, Tooltip, ButtonBase, Typography } from '@mui/material';

import { pressSx, motion as motionTokens } from '@nexus/ari-lib/theme/motion';
import { WorkspaceAvatar } from './workspace-avatar';
import { Workspace } from '../hooks/use-workspaces';
import { workspacePlanLabel } from '../lib/workspace-utils';

type WorkspaceSwitcherTriggerProps = {
  /** Null while the list is loading or failed to load. */
  workspace: Workspace | null;
  open: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
};

export function WorkspaceSwitcherTrigger({ workspace, open, onOpen }: WorkspaceSwitcherTriggerProps) {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <ButtonBase
      data-testid="cloud.workspace.switcher"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={onOpen}
      sx={{
        gap: 1.25,
        px: 1.25,
        py: 1,
        width: '100%',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'grey.800',
        color: 'grey.50',
        justifyContent: 'flex-start',
        '&:hover': { bgcolor: 'container.medium', borderColor: 'grey.700' },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: 2 },
        ...pressSx(reduceMotion),
        transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, border-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, transform ${motionTokens.duration.instant} ${motionTokens.ease.out}`,
      }}
    >
      {workspace ? (
        <WorkspaceAvatar workspace={workspace} />
      ) : (
        <Box aria-hidden sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: 'container.medium', flexShrink: 0 }} />
      )}

      <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
        <Tooltip title={workspace?.shortName ?? ''} placement="bottom">
          <Typography variant="subtitle2" noWrap sx={{ display: 'block', fontWeight: 700 }}>
            {workspace?.shortName ?? 'Workspace'}
          </Typography>
        </Tooltip>

        {workspace && (
          <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
            {workspacePlanLabel(workspace.subscriptionPlan)}
          </Typography>
        )}
      </Box>

      <ChevronDown size={16} aria-hidden />
    </ButtonBase>
  );
}
